import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";
import { columnFilterSchema } from "../../../common/columnFilterSchema";
import { cursorSchema } from "../../../common/cursorSchema";
import { statsSchema, merchStatsSchema } from "../../../common/statsSchema";
import { tagsSchema, tagsEnhancedSchema } from "../../../common/tagsSchema";

const goalsSchema = z.object({
  daily_sales_goal: z.number().nullable().optional(),
  daily_sales_goal_prev: z.number().nullable().optional(),
  daily_ad_spend_goal: z.number().nullable().optional(),
  daily_ad_spend_goal_prev: z.number().nullable().optional(),
  days_with_goal: z.number().nullable().optional(),
  days_with_goal_prev: z.number().nullable().optional(),
  sales_with_goal: z.number().nullable().optional(),
  sales_with_goal_prev: z.number().nullable().optional(),
  ad_spend_with_goal: z.number().nullable().optional(),
  ad_spend_with_goal_prev: z.number().nullable().optional(),
  total_acos_goal: z.number().nullable().optional(),
  total_acos_goal_prev: z.number().nullable().optional(),
  total_acos_with_goal: z.number().nullable().optional(),
  total_acos_with_goal_prev: z.number().nullable().optional(),
  total_sales_pacing_percent: z.number().nullable().optional(),
  total_sales_pacing_percent_prev: z.number().nullable().optional(),
  total_sales_pacing_offset: z.number().nullable().optional(),
  total_sales_pacing_offset_prev: z.number().nullable().optional(),
  ad_spend_pacing_percent: z.number().nullable().optional(),
  ad_spend_pacing_percent_prev: z.number().nullable().optional(),
  ad_spend_pacing_offset: z.number().nullable().optional(),
  ad_spend_pacing_offset_prev: z.number().nullable().optional(),
  tacos_pacing_percent: z.number().nullable().optional(),
  tacos_pacing_percent_prev: z.number().nullable().optional(),
  tacos_pacing_offset: z.number().nullable().optional(),
  tacos_pacing_offset_prev: z.number().nullable().optional(),
  avg_daily_sales_goal: z.number().nullable().optional(),
  avg_daily_sales_goal_prev: z.number().nullable().optional(),
  avg_daily_ad_spend_goal: z.number().nullable().optional(),
  avg_daily_ad_spend_goal_prev: z.number().nullable().optional(),
});

export const orgReportApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "getV4OrgReportByOrg",
    method: "get",
    path: "/v4/query/?table=org",
    parameters: [
      {
        name: "search",
        type: "Query",
        schema: z.string().array().optional(),
      },
      {
        name: "marketplace",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "grouping",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "start_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "start_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "all_active_orgs",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filters",
        type: "Query",
        schema: columnFilterSchema,
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    status: 200,
    response: z.object({
      data: z.array(
        z
          .object({
            org_id: z.number(),
            name: z.string(),
            market_place: z.string(),
            profile_type: z.string(),
            org_type: z.string(),
            amazon_profile_name: z.string(),
            org_is_visible: z.boolean(),
            tags: tagsSchema.nullable(),
            tags_enhanced: tagsEnhancedSchema,
          })
          .merge(statsSchema)
          .merge(merchStatsSchema)
          .merge(goalsSchema),
      ),
      cursor: cursorSchema,
      count: z.number(),
    }),
  })
    .addEndpoint({
      alias: "getV4OrgReportByDate",
      method: "get",
      path: "/v4/query/?table=org&grouping=start_date&limit=1000000&include_count=false",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },

        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "date_group",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
        {
          name: "all_active_orgs",
          type: "Query",
          schema: z.string().optional(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(
          z
            .object({
              start_date: z.string().date(),
            })
            .merge(statsSchema)
            .merge(merchStatsSchema),
        ),
        cursor: cursorSchema,
        count: z.number(),
      }),
    })
    .addEndpoint({
      alias: "getV4OrgReportTotals",
      method: "get",
      path: "/v4/query/aggregate/?table=org",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
        {
          name: "all_active_orgs",
          type: "Query",
          schema: z.string().optional(),
        },
      ],
      status: 200,
      response: z.object({
        data: statsSchema.merge(merchStatsSchema),
      }),
    })
    .addEndpoint({
      alias: "getV4OrgReportExport",
      method: "get",
      path: "/v4/query/?table=org&xls=true",
      parameters: [
        {
          name: "report_type",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.instanceof(Blob),
    })
    .addEndpoint({
      alias: "getV4OrgReportOptions",
      method: "get",
      path: "/v4/query/values/?table=org",
      parameters: [],
      status: 200,
      response: z.object({
        data: z.object({
          tags: z.array(z.string()),
        }),
      }),
    })
    .build(),
);
